import { useCallback, useMemo, useState } from 'react';

import type { ProductAccessRowConfig } from '../components/InviteTray/ProductAccessRow';
import type { ProductIconName } from '../components/InviteTray/ProductIcon';
import type { MemberRole, MemberRoleMap } from '../utils/constants';

import { getDefaultRoleValues } from './useRoles';

type ProductSelectionDefinition = {
  productKey: string;
  iconName: ProductIconName;
  iconBackground: string;
  title: string;
  description: string;
  hideIcon?: boolean;
};

type UseProductSelectionParams = {
  products: ProductSelectionDefinition[];
  rolesByProduct: Record<string, MemberRoleMap[]>;
};

type UseProductSelectionReturn = {
  accessRows: ProductAccessRowConfig[];
  selectedRoles: MemberRole[];
  selectedCount: number;
  totalCount: number;
  allProductsSelected: boolean;
  handleSelectAll: () => void;
  handleDeselectAll: () => void;
};

export function useProductSelection({
  products,
  rolesByProduct,
}: UseProductSelectionParams): UseProductSelectionReturn {
  const [rolesByProductSelection, setRolesByProductSelection] = useState<
    Record<string, MemberRole[]>
  >({});

  const handleProductCheckedChange = useCallback(
    (productKey: string, isChecked: boolean) => {
      setRolesByProductSelection((previous) => {
        if (isChecked) {
          if (previous[productKey]?.length) {
            return previous;
          }
          const defaultRoleValues = getDefaultRoleValues(rolesByProduct, productKey);
          if (defaultRoleValues.length === 0) {
            return previous;
          }
          return { ...previous, [productKey]: defaultRoleValues };
        }

        if (!previous[productKey]?.length) {
          return previous;
        }
        const { [productKey]: _removed, ...next } = previous;
        return next;
      });
    },
    [rolesByProduct],
  );

  const handleProductRoleToggle = useCallback(
    (productKey: string, role: MemberRole, isSelected: boolean) => {
      setRolesByProductSelection((previous) => {
        const current = previous[productKey] ?? [];

        if (isSelected) {
          if (current.includes(role)) {
            return previous;
          }
          return { ...previous, [productKey]: [...current, role] };
        }

        const filtered = current.filter((value) => value !== role);
        if (filtered.length === 0) {
          // Removing the last selected role also clears the product checkbox.
          const { [productKey]: _removed, ...next } = previous;
          return next;
        }
        return { ...previous, [productKey]: filtered };
      });
    },
    [],
  );

  const handleSelectAll = useCallback(() => {
    setRolesByProductSelection((previous) => {
      let hasChanges = false;
      const next = { ...previous };

      for (const { productKey } of products) {
        if (!next[productKey]?.length) {
          const defaultRoleValues = getDefaultRoleValues(rolesByProduct, productKey);
          if (defaultRoleValues.length > 0) {
            next[productKey] = defaultRoleValues;
            hasChanges = true;
          }
        }
      }

      return hasChanges ? next : previous;
    });
  }, [products, rolesByProduct]);

  const handleDeselectAll = useCallback(() => {
    setRolesByProductSelection({});
  }, []);

  const selectedCount = useMemo(
    () => products.filter(({ productKey }) => rolesByProductSelection[productKey]?.length).length,
    [products, rolesByProductSelection],
  );
  const allProductsSelected = products.length > 0 && selectedCount === products.length;

  // Each selected role already encodes its product and legal-entity scope in the role id.
  const selectedRoles = useMemo(
    () => products.flatMap(({ productKey }) => rolesByProductSelection[productKey] ?? []),
    [products, rolesByProductSelection],
  );

  const accessRows = useMemo(
    () =>
      products.map(
        ({ productKey, ...product }): ProductAccessRowConfig => ({
          ...product,
          productKey,
          onCheckedChange: (isChecked: boolean) =>
            handleProductCheckedChange(productKey, isChecked),
          roleOptions: rolesByProduct[productKey] ?? [],
          selectedRoleValues: rolesByProductSelection[productKey] ?? [],
          onRoleToggle: (role: MemberRole, isSelected: boolean) =>
            handleProductRoleToggle(productKey, role, isSelected),
        }),
      ),
    [
      handleProductCheckedChange,
      handleProductRoleToggle,
      products,
      rolesByProduct,
      rolesByProductSelection,
    ],
  );

  return {
    accessRows,
    selectedRoles,
    selectedCount,
    totalCount: products.length,
    allProductsSelected,
    handleSelectAll,
    handleDeselectAll,
  };
}
